import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import styles from './ManageTab.module.css';

/* Instructor-level default text for the two student-facing queue messages.
   A session can override these; blank here means the built-in system text. */
export default function QueueMessagesSection() {
  const { apiFetch } = useAuth();
  const [called, setCalled]     = useState('');
  const [nextUp, setNextUp]     = useState('');
  const [defaults, setDefaults] = useState({ called: '', next_up: '' });
  const [flash, setFlash]       = useState('');
  const [loaded, setLoaded]     = useState(false);
  const [saving, setSaving]     = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/queue/my-messages');
      if (res.ok) {
        const d = await res.json();
        setCalled(d.msg_called || '');
        setNextUp(d.msg_next_up || '');
        setDefaults({ called: d.default_called || '', next_up: d.default_next_up || '' });
      }
    } catch { /* leave blank */ }
    setLoaded(true);
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await apiFetch('/api/queue/my-messages', {
        method: 'PUT',
        body: JSON.stringify({ msg_called: called, msg_next_up: nextUp }),
      });
      const d = await res.json().catch(() => ({}));
      setFlash(res.ok ? (d.message || 'Saved.') : (d.detail || 'Could not save.'));
      setTimeout(() => setFlash(''), 3000);
    } finally { setSaving(false); }
  };

  const field = (label, value, setValue, fallback, help) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontSize: 11, color: 'var(--txt-faint)', display: 'block', marginBottom: 4 }}>
        {label}
      </label>
      <input
        type="text"
        value={value}
        maxLength={300}
        placeholder={fallback}
        onChange={e => setValue(e.target.value)}
        style={{ width: '100%', fontSize: 13, padding: '6px 9px' }}
      />
      <div style={{ fontSize: 11, color: 'var(--txt-faint)', marginTop: 4, lineHeight: 1.4 }}>
        {help} {!value.trim() && <>Currently using the default: <em>{fallback}</em></>}
      </div>
    </div>
  );

  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>Queue Messages</h3>
      <p style={{ fontSize: 12, color: 'var(--txt-dim)', marginBottom: 12, lineHeight: 1.5 }}>
        What students see in the queue. These are your defaults for every session —
        you can override them for a single session from the queue panel. Leave a
        field blank to use the system default.
      </p>
      {flash && <div style={{ fontSize: 12, color: 'var(--green)', marginBottom: 10 }}>{flash}</div>}

      {!loaded ? (
        <div style={{ fontSize: 13, color: 'var(--txt-faint)' }}>Loading…</div>
      ) : (
        <>
          {field(
            'When a student is called',
            called, setCalled, defaults.called,
            'Shown to the student the moment you call them.'
          )}
          {field(
            'When a student is next up (on deck)',
            nextUp, setNextUp, defaults.next_up,
            'Shown to whoever is first in line, before they are called.'
          )}
          <button className="btn-primary" onClick={save} disabled={saving} style={{ fontSize: 13 }}>
            {saving ? 'Saving…' : 'Save messages'}
          </button>
        </>
      )}
    </section>
  );
}
